import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatchSelection, Scenario } from '../../services/stage-state.service';
import { BenefitApiService } from '../../../../services/benefit-api.service';
import { ProductType } from './broker-profile.models';
import { BrokerProfileRateHistoryComponent } from './broker-profile-rate-history.component';

interface InterestRow {
  credstrat?: string;
  indexused?: string;
  curcap?: number | null;
  curparrat?: number | null;
  curspread?: number | null;
  stratfee?: number | null;
}

interface ChargesData {
  schedule: { yr: number; pct: number }[];
  waivers: any[];
}

interface RidersData {
  gmwbCount: number;
  deathCount: number;
  bonusCount: number;
  gmabCount: number;
  gmibCount: number;
  featuresCount: number;
  selectedRider: any | null;
}

interface OptionsData {
  withdrawal: any | null;
  settlement: any | null;
}

interface StatesData {
  sections: { key: string; label: string; stateLogic: string; cases?: any[] }[];
}

interface PeerCompareRow {
  key: 'income' | 'fee' | 'surrender';
  label: string;
  thisText: string;
  peerText: string;
  deltaText: string;
  direction: 'pos' | 'neg';
  fillPct: number;
}

/**
 * Case-mode body for the broker profile flyout. Renders the full Fit zone
 * (scenario strip → hero ring → two-col radar + income → rationale → peer
 * bars) above a Product zone (rate callout → feature tiles → accordion).
 *
 * Data is derived from the MatchSelection + peers + scenario passed via
 * inputs. No API calls here in Phase 1; accordion sections render a shell
 * that Phase 2 hydrates with BenefitApi content.
 */
@Component({
  selector: 'app-broker-profile-case-body',
  standalone: true,
  imports: [CommonModule, BrokerProfileRateHistoryComponent],
  template: `
    <!-- Scenario strip ────────────────────────────────────── -->
    <div class="scen-strip">
      <span class="scen-lbl">Scenario</span>
      @if (scenario(); as s) {
        <span class="scen-chip"><b>{{ money(s.contribution) }}</b>premium</span>
        <span class="scen-chip"><b>{{ s.deferralYear }}yr</b>deferral</span>
        <span class="scen-chip"><b>{{ s.ownership }}</b>
          {{ s.clientAge }}@if (s.spouseAge) { / {{ s.spouseAge }} }</span>
        <span class="scen-chip"><b>{{ s.stateCode }}</b>state</span>
      } @else {
        <span class="scen-chip scen-chip--dim">No scenario bound</span>
      }
      <a class="scen-edit">Edit in Stage 02</a>
    </div>

    <!-- Hero: ring + headline + CTA ─────────────────────────── -->
    <div class="hero">
      <div class="ring-wrap">
        <div class="ring-lg">
          <svg width="130" height="130" viewBox="0 0 130 130">
            <circle cx="65" cy="65" r="58" fill="none" stroke="var(--border)" stroke-width="7"/>
            <circle cx="65" cy="65" r="58" fill="none"
                    [attr.stroke]="tierColor()" stroke-width="7"
                    stroke-dasharray="364"
                    [attr.stroke-dashoffset]="364 - (364 * fitTotal() / 100)"
                    stroke-linecap="round"/>
          </svg>
          <div class="ring-num">{{ fitTotal() }}</div>
        </div>
        <div class="ring-cap">Fit Score</div>
        <div class="ring-tier" [style.color]="tierColor()">{{ tierLabel() }}</div>
      </div>

      <div class="hero-mid">
        <div class="hero-eyebrow">Ranked #{{ rank() }} of {{ peers().length + 1 }} for this client</div>
        <h2 class="hero-title">{{ headline() }}</h2>
        <p class="hero-sub" [innerHTML]="subhead()"></p>
      </div>

      <div class="hero-cta">
        <button class="btn primary" (click)="illustrate.emit()">
          Illustrate this product →
        </button>
        <button class="btn ghost" (click)="backToMatch.emit()">← Back to Top 5</button>
      </div>
    </div>

    <!-- Two-col: radar + income ─────────────────────────────── -->
    <div class="two">
      <div class="card">
        <div class="card-title-row">
          <div class="card-title">Fit profile</div>
          <div class="aux">4-axis radar</div>
        </div>
        <svg class="radar-lg" viewBox="0 0 220 220">
          <!-- Axes -->
          <polygon points="110,15 205,110 110,205 15,110" fill="none" stroke="var(--border-light)" stroke-width="1"/>
          <polygon points="110,40 180,110 110,180 40,110" fill="none" stroke="var(--border-light)" stroke-width="1"/>
          <polygon points="110,65 155,110 110,155 65,110" fill="none" stroke="var(--border-light)" stroke-width="1"/>
          <line x1="110" y1="15" x2="110" y2="205" stroke="var(--border-light)" stroke-width="1"/>
          <line x1="15" y1="110" x2="205" y2="110" stroke="var(--border-light)" stroke-width="1"/>
          <!-- Top peer silhouette (faded) -->
          @if (topPeerPolygon(); as tp) {
            <polygon [attr.points]="tp" fill="rgba(31,107,82,0.07)"
                     stroke="var(--pos)" stroke-width="1" stroke-dasharray="3,3"/>
          }
          <!-- This product -->
          <polygon [attr.points]="thisPolygon()" fill="var(--blue-14)"
                   stroke="var(--blue-2)" stroke-width="2"/>
          <!-- Axis labels -->
          <text x="110" y="10" text-anchor="middle" class="radar-lbl">INCOME</text>
          <text x="214" y="113" text-anchor="end" class="radar-lbl">COST</text>
          <text x="110" y="218" text-anchor="middle" class="radar-lbl">CARRIER</text>
          <text x="6" y="113" class="radar-lbl">FLEX</text>
          <circle cx="110" cy="110" r="2" fill="var(--muted)"/>
        </svg>
        <div class="pc-legend">
          <span class="this">This pick</span>
          @if (topPeerPolygon()) {
            <span class="top">{{ isTopRanked() ? 'Runner-up' : 'Top fit (#1)' }}</span>
          }
        </div>
      </div>

      <div class="card">
        <div class="card-title-row">
          <div class="card-title">Scenario-bound income</div>
          <div class="aux">bound to THIS client · not marketing numbers</div>
        </div>
        <div class="inc-grid">
          <div class="inc-tile hi">
            <div class="inc-lbl">Est. annual income</div>
            <div class="inc-val">{{ money(product().estAnnualIncome) }}</div>
            <div class="inc-sub">
              {{ scenario()?.ownership?.toLowerCase() ?? 'single' }} life
              @if (incomeVsTop() !== null) {
                · <span class="inc-delta" [class.down]="incomeVsTop()! < 0">
                  {{ incomeVsTop()! >= 0 ? '+' : '' }}{{ money(incomeVsTop()!) }} vs #1
                </span>
              }
            </div>
          </div>
          <div class="inc-tile">
            <div class="inc-lbl">Rider fee</div>
            <div class="inc-val">{{ product().riderFee.toFixed(2) }}%</div>
            <div class="inc-sub">annual, off benefit base</div>
          </div>
          <div class="inc-tile">
            <div class="inc-lbl">Surrender term</div>
            <div class="inc-val">{{ product().surryr }}<span class="inc-unit">yr</span></div>
            <div class="inc-sub">walkaway penalty period</div>
          </div>
        </div>
        <div class="inc-foot">
          Benefit base: <b>{{ product().benifitBaseType || '—' }}</b>
          @if (product().rollupPer) { · Rollup <b>{{ product().rollupPer }}</b>
            @if (product().maxRollupYears) { for <b>{{ product().maxRollupYears }}yr</b> }
          }
        </div>
      </div>
    </div>

    <!-- Rationale bullets ──────────────────────────────────── -->
    @if (rationaleItems().length > 0) {
      <div class="card rat-card">
        <div class="card-title-row">
          <div class="card-title">Why this ranked here</div>
          <div class="aux">from Fit Score breakdown</div>
        </div>
        <div class="rat">
          @for (b of rationaleItems(); track $index) {
            <div class="rat-b" [class]="'rat-b--' + b.tone">
              <span class="rat-icon">{{ b.tone === 'win' ? '✓' : b.tone === 'mix' ? '~' : '!' }}</span>
              <span class="rat-txt" [innerHTML]="b.text"></span>
            </div>
          }
        </div>
      </div>
    }

    <!-- Peer-compare bars ──────────────────────────────────── -->
    @if (peerCompareRows().length > 0) {
      <div class="card pc-card">
        <div class="card-title-row">
          <div class="card-title">This pick vs best peer</div>
          <div class="aux">{{ bestPeerLabel() }}</div>
        </div>
        <div class="pc-rows">
          @for (row of peerCompareRows(); track row.key) {
            <div class="pc-row" [class.pc-row--neg]="row.direction === 'neg'">
              <div class="pc-head">
                <span class="pc-lbl">{{ row.label }}</span>
                <span class="pc-delta">
                  <b>{{ row.deltaText }}</b>
                  <span class="pc-vs">{{ row.thisText }} vs {{ row.peerText }}</span>
                </span>
              </div>
              <div class="pc-track">
                <div class="pc-fill"
                     [style.width.%]="row.fillPct"
                     [class.pc-fill--neg]="row.direction === 'neg'"></div>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- Zone divider ───────────────────────────────────────── -->
    <div class="zone-div">
      <span class="zone-lbl">Product details</span>
    </div>

    <!-- Rate callout (Product zone top) ────────────────────── -->
    <div class="rate-card">
      <div class="rate-bar" [style.background]="tierColor()"></div>
      <div class="rate-inner">
        <div>
          <div class="rate-kicker">Rider factor @ {{ scenario()?.deferralYear ?? 10 }}yr</div>
          <div class="rate-big">{{ (factor() * 100).toFixed(1) }}<span class="pct">%</span></div>
        </div>
        <div class="rate-side">
          <div>{{ scenario()?.ownership === 'Joint' ? 'joint' : 'single' }} life</div>
          <div>rollup {{ product().rollupPer || '—' }}</div>
        </div>
      </div>
    </div>

    <!-- Feature tiles 2×3 ──────────────────────────────────── -->
    <div class="tiles">
      <div class="tile pos">
        <div class="tile-lbl">Surrender</div>
        <div class="tile-val">{{ product().surryr }}yr</div>
      </div>
      <div class="tile neu">
        <div class="tile-lbl">Rider fee</div>
        <div class="tile-val">{{ product().riderFee.toFixed(2) }}%</div>
      </div>
      <div class="tile neu">
        <div class="tile-lbl">Benefit base</div>
        <div class="tile-val word">{{ product().benifitBaseType || '—' }}</div>
      </div>
      <div class="tile pos">
        <div class="tile-lbl">AM Best</div>
        <div class="tile-val">{{ product().bestid || '—' }}</div>
      </div>
      <div class="tile neu">
        <div class="tile-lbl">Rollup</div>
        <div class="tile-val">{{ product().rollupPer || '—' }}</div>
        @if (product().maxRollupYears) {
          <div class="tile-sub">max {{ product().maxRollupYears }}yr</div>
        }
      </div>
      <div class="tile neu">
        <div class="tile-lbl">Rider</div>
        <div class="tile-val word">{{ product().riderName }}</div>
      </div>
    </div>

    <!-- Rate history sparkline ───────────────────────────── -->
    <app-broker-profile-rate-history
      [productType]="productType()"
      [productid]="product().productid"
      variant="wide" />

    <!-- Accordion ──────────────────────────────────────────── -->
    <div class="accord-intro">
      Tap a section for live product detail. "Open full …" jumps to the reference tab.
    </div>
    @for (sec of accordionSections; track sec.id) {
      <div class="acc" [class.open]="openSections().has(sec.id)">
        <div class="acc-hd" (click)="toggleSection(sec.id)">
          <span class="acc-lbl">{{ sec.label }}</span>
          <span class="acc-summary">{{ sec.summary }}</span>
          <span class="acc-chev">▾</span>
        </div>
        @if (openSections().has(sec.id)) {
          <div class="acc-body">
            @switch (sec.id) {
              @case ('interest') {
                @if (interestLoading()) { <div class="acc-loading">Loading strategies…</div> }
                @else if (interestData().length === 0) { <div class="acc-empty">No interest strategies on file.</div> }
                @else {
                  <table class="acc-tbl">
                    <thead>
                      <tr>
                        <th>Strategy</th><th>Index</th>
                        <th class="num">Cap</th><th class="num">Par</th><th class="num">Spread</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (r of interestData(); track $index) {
                        <tr>
                          <td>{{ (r.credstrat || '—').trim() }}</td>
                          <td class="muted">{{ (r.indexused || '—').trim() }}</td>
                          <td class="num">{{ pct(r.curcap) }}</td>
                          <td class="num">{{ pct(r.curparrat) }}</td>
                          <td class="num">{{ pct(r.curspread) }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                }
              }
              @case ('riders') {
                @if (ridersLoading()) { <div class="acc-loading">Loading riders…</div> }
                @else if (!ridersData()) { <div class="acc-empty">No rider data on file.</div> }
                @else {
                  <div class="rider-counts">
                    <span class="rc-chip"><b>{{ ridersData()!.gmwbCount }}</b>GMWB</span>
                    <span class="rc-chip"><b>{{ ridersData()!.deathCount }}</b>Death benefit</span>
                    <span class="rc-chip"><b>{{ ridersData()!.bonusCount }}</b>Bonus</span>
                    <span class="rc-chip"><b>{{ ridersData()!.gmabCount }}</b>GMAB</span>
                    <span class="rc-chip"><b>{{ ridersData()!.gmibCount }}</b>GMIB</span>
                    <span class="rc-chip"><b>{{ ridersData()!.featuresCount }}</b>Features</span>
                  </div>
                  @if (ridersData()!.selectedRider; as r) {
                    <div class="rider-panel">
                      <div class="rider-name">{{ r.name }} <span class="rider-meta">· GMWB #{{ r.pwGmwbId }}</span></div>
                      @if (r.benefitDescription) {
                        <div class="rider-desc">{{ r.benefitDescription }}</div>
                      }
                    </div>
                  }
                }
              }
              @case ('charges') {
                @if (chargesLoading()) { <div class="acc-loading">Loading surrender + waiver data…</div> }
                @else if (!chargesData()) { <div class="acc-empty">No surrender or waiver data on file.</div> }
                @else {
                  @if (chargesData()!.schedule.length > 0) {
                    <div class="acc-subhead">Surrender schedule</div>
                    <div class="surr-grid">
                      @for (y of chargesData()!.schedule; track y.yr) {
                        <div class="surr-cell" [class.surr-zero]="y.pct === 0">
                          <div class="surr-yr">Yr {{ y.yr }}</div>
                          <div class="surr-pct">{{ y.pct === 0 ? '—' : y.pct.toFixed(1) + '%' }}</div>
                        </div>
                      }
                    </div>
                  }
                  @if (chargesData()!.waivers.length > 0) {
                    <div class="acc-subhead">Waivers</div>
                    <ul class="waiver-list">
                      @for (w of chargesData()!.waivers; track $index) {
                        <li><b>{{ w.waiverName || w.waivType || '—' }}</b>
                          @if (w.eliminationPeriod) { · elim {{ w.eliminationPeriod }} }
                          @if (w.contractInForce) { · in force {{ w.contractInForce }} }
                        </li>
                      }
                    </ul>
                  }
                  @if (chargesData()!.schedule.length === 0 && chargesData()!.waivers.length === 0) {
                    <div class="acc-empty">Nothing recorded.</div>
                  }
                }
              }
              @case ('options') {
                @if (optionsLoading()) { <div class="acc-loading">Loading annuity options…</div> }
                @else if (!optionsData()) { <div class="acc-empty">No option data on file.</div> }
                @else {
                  @if (optionsData()!.withdrawal) {
                    <div class="acc-subhead">Free withdrawal</div>
                    <div class="opt-row">
                      <span class="opt-k">Annual free</span>
                      <span class="opt-v">{{ fmtLiteralPct(optionsData()!.withdrawal!.annfreepct) }}</span>
                    </div>
                    @if (optionsData()!.withdrawal!.desAnnfre) {
                      <div class="opt-note">{{ optionsData()!.withdrawal!.desAnnfre }}</div>
                    }
                  }
                  @if (optionsData()!.settlement) {
                    <div class="acc-subhead">Settlement</div>
                    @if (optionsData()!.settlement!.desMinage; as note) {
                      <div class="opt-note">{{ note }}</div>
                    } @else {
                      <div class="opt-empty">Settlement terms on file — see full reference tab.</div>
                    }
                  }
                }
              }
              @case ('states') {
                @if (statesLoading()) { <div class="acc-loading">Loading state availability…</div> }
                @else if (!statesData()) { <div class="acc-empty">No state variation data on file.</div> }
                @else {
                  <div class="acc-subhead">State logic by benefit section</div>
                  <ul class="states-list">
                    @for (s of statesData()!.sections; track s.key) {
                      <li>
                        <span class="states-label">{{ s.label }}</span>
                        <span class="states-logic" [class.states-logic--ok]="s.stateLogic === 'available'">{{ s.stateLogic }}</span>
                        <span class="states-cases">{{ s.cases?.length ?? 0 }} case{{ (s.cases?.length ?? 0) === 1 ? '' : 's' }}</span>
                      </li>
                    }
                  </ul>
                }
              }
            }
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      font-family: var(--sans);
      color: var(--text);
    }

    /* Scenario strip */
    .scen-strip {
      display: flex; align-items: center; gap: 8px;
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 8px 12px;
      margin-bottom: 14px;
    }
    .scen-lbl {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.1em; text-transform: uppercase;
    }
    .scen-chip {
      display: inline-flex; align-items: center; gap: 6px;
      background: var(--blue-10);
      color: var(--navy);
      padding: 4px 10px;
      border-radius: 3px;
      font-size: 12px; font-weight: 600;
    }
    .scen-chip b {
      font-family: var(--mono);
      font-weight: 700;
      color: var(--blue);
    }
    .scen-chip--dim {
      background: var(--panel2);
      color: var(--muted);
      font-style: italic;
    }
    .scen-edit {
      margin-left: auto;
      font-size: 11px;
      color: var(--blue-2);
      font-weight: 600;
      cursor: pointer;
      letter-spacing: 0.02em;
    }
    .scen-edit::after { content: ' →'; }

    /* Hero */
    .hero {
      display: grid;
      grid-template-columns: 160px 1fr 200px;
      gap: 18px;
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 22px 20px;
      margin-bottom: 14px;
      align-items: center;
    }
    .ring-wrap { display: flex; flex-direction: column; align-items: center; gap: 5px; }
    .ring-lg { position: relative; width: 130px; height: 130px; }
    .ring-lg svg { transform: rotate(-90deg); }
    .ring-num {
      position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
      font-family: var(--mono);
      font-weight: 700; font-size: 38px;
      color: var(--navy);
      letter-spacing: -0.02em;
    }
    .ring-cap {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em; text-transform: uppercase;
    }
    .ring-tier {
      font-size: 11px; font-weight: 700;
      letter-spacing: 0.04em;
    }
    .hero-mid { min-width: 0; }
    .hero-eyebrow {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em; text-transform: uppercase;
      margin-bottom: 4px;
    }
    .hero-title {
      font-size: 20px; font-weight: 700;
      color: var(--navy);
      margin: 0;
      letter-spacing: -0.015em;
    }
    .hero-sub {
      font-size: 13px;
      color: var(--text);
      margin-top: 8px;
      line-height: 1.45;
    }
    .hero-sub :is(b, strong) {
      color: var(--navy);
      font-weight: 700;
    }
    .hero-cta { display: flex; flex-direction: column; gap: 8px; }

    .btn {
      font-family: var(--sans);
      font-size: 13px; font-weight: 600;
      padding: 10px 14px;
      border: 1px solid var(--border);
      border-radius: 3px;
      background: var(--panel);
      color: var(--text);
      cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
      gap: 8px;
    }
    .btn.primary {
      background: var(--navy);
      color: #fff;
      border-color: var(--navy);
    }
    .btn.primary:hover { background: var(--navy2); }
    .btn.ghost {
      background: transparent;
      color: var(--muted);
      border-color: var(--border-light);
    }

    /* Two-col radar + income */
    .two {
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 14px;
      margin-bottom: 14px;
    }
    .card {
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 16px 18px;
    }
    .card-title-row { display: flex; align-items: baseline; margin-bottom: 12px; }
    .card-title {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em; text-transform: uppercase;
    }
    .aux {
      margin-left: auto;
      font-family: var(--mono);
      font-size: 10px; font-weight: 600;
      color: var(--label);
      letter-spacing: 0.04em;
    }
    .radar-lg { width: 220px; height: 220px; display: block; margin: 0 auto; }
    .radar-lbl {
      font-family: var(--mono);
      font-size: 9px;
      font-weight: 700;
      fill: var(--muted);
      letter-spacing: 0.1em;
    }
    .pc-legend {
      display: flex; gap: 14px; justify-content: center;
      font-size: 11px;
      color: var(--muted);
      margin-top: 6px;
    }
    .pc-legend .this::before {
      content: ''; display: inline-block;
      width: 10px; height: 10px;
      background: var(--blue-2);
      margin-right: 5px; vertical-align: middle;
      border-radius: 1px;
    }
    .pc-legend .top::before {
      content: ''; display: inline-block;
      width: 14px; border-bottom: 1.5px dashed var(--pos);
      margin-right: 5px; vertical-align: middle;
    }

    /* Scenario-bound income */
    .inc-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }
    .inc-tile {
      padding: 10px 12px;
      background: var(--panel2);
      border: 1px solid var(--border-light);
      border-radius: 3px;
      border-left: 3px solid var(--blue-2);
    }
    .inc-tile.hi { border-left-color: var(--pos); }
    .inc-lbl {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em; text-transform: uppercase;
      margin-bottom: 4px;
    }
    .inc-val {
      font-family: var(--mono);
      font-size: 28px; font-weight: 700;
      color: var(--navy);
      letter-spacing: -0.02em;
    }
    .inc-unit {
      font-size: 14px;
      color: var(--muted);
      margin-left: 3px;
      font-weight: 500;
    }
    .inc-sub {
      font-size: 11px;
      color: var(--muted);
      margin-top: 3px;
    }
    .inc-delta {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--pos);
    }
    .inc-delta.down { color: var(--neg); }
    .inc-foot {
      margin-top: 12px;
      padding-top: 10px;
      border-top: 1px dashed var(--border-light);
      font-size: 11px;
      color: var(--muted);
    }
    .inc-foot b {
      color: var(--navy);
      font-weight: 700;
    }

    /* Rationale */
    .rat-card { margin-bottom: 14px; }

    /* Peer-compare bars */
    .pc-card { margin-bottom: 14px; }
    .pc-rows {
      display: flex; flex-direction: column;
      gap: 10px;
    }
    .pc-row { display: flex; flex-direction: column; gap: 4px; }
    .pc-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 8px;
    }
    .pc-lbl {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }
    .pc-delta {
      display: inline-flex;
      align-items: baseline;
      gap: 8px;
      font-family: var(--mono);
      font-size: 11px;
      color: var(--muted);
    }
    .pc-delta b {
      font-family: var(--mono);
      font-size: 13px;
      font-weight: 700;
      color: var(--pos);
      letter-spacing: -0.01em;
    }
    .pc-row--neg .pc-delta b { color: var(--neg); }
    .pc-vs {
      font-family: var(--mono);
      font-size: 10px;
      color: var(--label);
      letter-spacing: 0.02em;
    }
    .pc-track {
      height: 6px;
      background: var(--border-light);
      border-radius: 3px;
      overflow: hidden;
    }
    .pc-fill {
      height: 100%;
      background: var(--pos);
      transition: width 0.2s ease;
    }
    .pc-fill--neg {
      background: var(--neg);
    }
    .rat { display: flex; flex-direction: column; gap: 8px; }
    .rat-b {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 8px 10px;
      background: var(--panel2);
      border-radius: 3px;
      border-left: 3px solid var(--border);
    }
    .rat-b--win { border-left-color: var(--pos); }
    .rat-b--mix { border-left-color: var(--amber); }
    .rat-b--miss { border-left-color: var(--neg); }
    .rat-icon {
      width: 18px; height: 18px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      font-size: 11px; font-weight: 700;
      font-family: var(--mono);
    }
    .rat-b--win .rat-icon { background: var(--pos-10); color: var(--pos); }
    .rat-b--mix .rat-icon { background: var(--amber-10); color: var(--amber); }
    .rat-b--miss .rat-icon { background: var(--neg-10); color: var(--neg); }
    .rat-txt {
      font-size: 13px;
      color: var(--text);
      line-height: 1.4;
    }

    /* Zone divider */
    .zone-div {
      display: flex; align-items: center; gap: 12px;
      margin: 18px 2px 10px;
    }
    .zone-div::before, .zone-div::after {
      content: ''; flex: 1;
      border-top: 1px dashed var(--border);
    }
    .zone-lbl {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.14em; text-transform: uppercase;
    }

    /* Rate callout */
    .rate-card {
      display: grid;
      grid-template-columns: 5px 1fr;
      background: var(--panel2);
      border: 1px solid var(--border);
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 12px;
    }
    .rate-bar { background: var(--pos); }
    .rate-inner {
      padding: 12px 16px;
      display: flex; align-items: baseline;
      gap: 14px;
    }
    .rate-kicker {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.14em; text-transform: uppercase;
    }
    .rate-big {
      font-family: var(--mono);
      font-size: 36px; font-weight: 700;
      color: var(--navy);
      letter-spacing: -0.03em;
      line-height: 0.95;
      margin-top: 2px;
    }
    .rate-big .pct {
      font-size: 20px;
      color: var(--muted);
      margin-left: 3px;
      font-weight: 500;
    }
    .rate-side {
      margin-left: auto;
      text-align: right;
      font-family: var(--mono);
      font-size: 10px;
      color: var(--muted);
      letter-spacing: 0.02em;
    }

    /* Feature tiles */
    .tiles {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-bottom: 12px;
    }
    .tile {
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 3px;
      padding: 10px 12px;
      min-height: 72px;
    }
    .tile.pos { border-left: 3px solid var(--pos); }
    .tile.neu { border-left: 3px solid var(--blue-2); }
    .tile.warn { border-left: 3px solid var(--amber); }
    .tile-lbl {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em; text-transform: uppercase;
    }
    .tile-val {
      font-size: 15px; font-weight: 700;
      color: var(--navy);
      margin-top: 4px;
      letter-spacing: -0.01em;
      font-family: var(--mono);
    }
    .tile-val.word {
      font-family: var(--sans);
      font-size: 13px;
      letter-spacing: 0;
    }
    .tile-sub {
      font-size: 10px;
      color: var(--muted);
      margin-top: 2px;
      line-height: 1.3;
    }

    /* Accordion */
    .accord-intro {
      font-size: 11px;
      color: var(--muted);
      margin: 6px 2px 10px;
      font-style: italic;
    }
    .acc {
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 4px;
      margin-bottom: 8px;
      overflow: hidden;
    }
    .acc-hd {
      display: flex; align-items: center; gap: 12px;
      padding: 10px 14px;
      cursor: pointer;
      user-select: none;
      transition: background 0.1s;
    }
    .acc-hd:hover { background: var(--panel2); }
    .acc.open .acc-hd {
      background: var(--panel2);
      border-bottom: 1px solid var(--border-light);
    }
    .acc-lbl {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--navy);
      letter-spacing: 0.12em; text-transform: uppercase;
    }
    .acc-summary {
      font-size: 12px; font-weight: 500;
      color: var(--muted);
    }
    .acc-chev {
      margin-left: auto;
      color: var(--muted);
      transition: transform 0.15s;
      font-size: 14px;
      line-height: 1;
      width: 14px;
      text-align: center;
    }
    .acc.open .acc-chev {
      transform: rotate(180deg);
      color: var(--blue-2);
    }
    .acc-body {
      padding: 12px 14px 14px;
      background: var(--panel);
    }
    .acc-loading {
      font-size: 12px;
      color: var(--muted);
      font-style: italic;
    }
    .acc-empty {
      font-size: 12px;
      color: var(--muted);
      font-style: italic;
    }
    .acc-subhead {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em; text-transform: uppercase;
      margin: 10px 0 6px;
    }
    .acc-subhead:first-child { margin-top: 0; }

    /* Interest strategy table */
    .acc-tbl {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 12px;
    }
    .acc-tbl thead th {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.1em; text-transform: uppercase;
      text-align: left;
      padding: 4px 8px 6px 0;
      border-bottom: 1px solid var(--border-light);
    }
    .acc-tbl thead th.num { text-align: right; }
    .acc-tbl tbody td {
      padding: 6px 8px 6px 0;
      border-bottom: 1px solid var(--border-light);
      color: var(--text);
    }
    .acc-tbl tbody tr:last-child td { border-bottom: none; }
    .acc-tbl .muted { color: var(--muted); }
    .acc-tbl .num {
      text-align: right;
      font-family: var(--mono);
      font-weight: 600;
      color: var(--navy);
    }

    /* Rider counts row */
    .rider-counts {
      display: flex; flex-wrap: wrap; gap: 6px;
      margin-bottom: 10px;
    }
    .rc-chip {
      font-size: 11px; font-weight: 600;
      color: var(--muted);
      background: var(--panel2);
      border: 1px solid var(--border-light);
      padding: 3px 8px;
      border-radius: 3px;
    }
    .rc-chip b {
      font-family: var(--mono);
      font-weight: 700;
      color: var(--navy);
      margin-right: 4px;
    }
    .rider-panel {
      background: var(--panel2);
      border: 1px solid var(--border-light);
      border-left: 3px solid var(--blue-2);
      border-radius: 3px;
      padding: 10px 12px;
    }
    .rider-name {
      font-size: 13px; font-weight: 700;
      color: var(--navy);
    }
    .rider-meta {
      font-family: var(--mono);
      font-size: 10px; font-weight: 500;
      color: var(--muted);
      letter-spacing: 0.02em;
    }
    .rider-desc {
      margin-top: 6px;
      font-size: 12px;
      color: var(--text);
      line-height: 1.4;
    }

    /* Surrender schedule grid */
    .surr-grid {
      display: grid;
      grid-template-columns: repeat(10, 1fr);
      gap: 4px;
    }
    .surr-cell {
      background: var(--panel2);
      border: 1px solid var(--border-light);
      border-radius: 2px;
      padding: 4px 2px;
      text-align: center;
    }
    .surr-cell.surr-zero { opacity: 0.4; }
    .surr-yr {
      font-family: var(--mono);
      font-size: 9px;
      color: var(--muted);
      letter-spacing: 0.04em;
    }
    .surr-pct {
      font-family: var(--mono);
      font-size: 12px; font-weight: 700;
      color: var(--navy);
    }

    .waiver-list {
      margin: 4px 0 0; padding: 0;
      list-style: none;
      font-size: 12px;
      color: var(--text);
    }
    .waiver-list li {
      padding: 4px 0;
      border-bottom: 1px dashed var(--border-light);
    }
    .waiver-list li:last-child { border-bottom: none; }
    .waiver-list b {
      color: var(--navy);
      font-weight: 700;
    }

    /* Annuity options */
    .opt-row {
      display: flex; align-items: baseline;
      padding: 4px 0;
      border-bottom: 1px dashed var(--border-light);
    }
    .opt-row:last-child { border-bottom: none; }
    .opt-k {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.1em;
      text-transform: uppercase;
      flex: 1;
    }
    .opt-v {
      font-family: var(--mono);
      font-size: 13px; font-weight: 700;
      color: var(--navy);
    }
    .opt-note {
      font-size: 12px;
      color: var(--text);
      margin-top: 4px;
      line-height: 1.4;
    }
    .opt-empty {
      font-size: 12px;
      color: var(--muted);
      font-style: italic;
    }

    /* States list */
    .states-list {
      list-style: none;
      padding: 0; margin: 0;
      font-size: 12px;
    }
    .states-list li {
      display: grid;
      grid-template-columns: 1fr auto auto;
      gap: 10px;
      align-items: baseline;
      padding: 5px 0;
      border-bottom: 1px dashed var(--border-light);
    }
    .states-list li:last-child { border-bottom: none; }
    .states-label {
      color: var(--text);
      font-weight: 600;
    }
    .states-logic {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.06em; text-transform: uppercase;
    }
    .states-logic--ok { color: var(--pos); }
    .states-cases {
      font-family: var(--mono);
      font-size: 10px;
      color: var(--muted);
      min-width: 46px;
      text-align: right;
    }
  `],
})
export class BrokerProfileCaseBodyComponent {
  private readonly api = inject(BenefitApiService);

  readonly product = input.required<MatchSelection>();
  readonly peers = input<MatchSelection[]>([]);
  readonly scenario = input<Scenario | null>(null);
  readonly rank = input<number>(1);
  readonly productType = input<ProductType>('ia');

  readonly illustrate = output();
  readonly backToMatch = output();

  private readonly _openSections = signal<Set<string>>(new Set());
  readonly openSections = this._openSections.asReadonly();

  readonly accordionSections = [
    { id: 'interest',  label: 'Interest',         summary: 'Strategies, caps, par rates, spreads' },
    { id: 'riders',    label: 'Riders',           summary: 'Benefit, death, bonus, GMWB details' },
    { id: 'charges',   label: 'Charges',          summary: 'Surrender schedule + waivers' },
    { id: 'options',   label: 'Annuity Options',  summary: 'Free withdrawal, settlement' },
    { id: 'states',    label: 'States',           summary: 'Availability by benefit section' },
  ];

  // Accordion data — lazy-loaded on first expand, cached per-component-instance.
  // The underlying BenefitApiService also caches across components via shareReplay.
  readonly interestData = signal<InterestRow[]>([]);
  readonly interestLoading = signal(false);
  readonly chargesData = signal<ChargesData | null>(null);
  readonly chargesLoading = signal(false);
  readonly ridersData = signal<RidersData | null>(null);
  readonly ridersLoading = signal(false);
  readonly optionsData = signal<OptionsData | null>(null);
  readonly optionsLoading = signal(false);
  readonly statesData = signal<StatesData | null>(null);
  readonly statesLoading = signal(false);

  private readonly hydrated = new Set<string>();

  readonly fitTotal = computed(() => this.product().fit.total);

  readonly tierColor = computed(() => {
    const t = this.fitTotal();
    if (t >= 90) return 'var(--pos)';
    if (t >= 80) return 'var(--blue-2)';
    if (t >= 70) return 'var(--amber)';
    return 'var(--neg)';
  });

  readonly tierLabel = computed(() => {
    const t = this.fitTotal();
    if (t >= 90) return 'Top-tier fit';
    if (t >= 80) return 'Strong fit';
    if (t >= 70) return 'Fair fit';
    return 'Weak fit';
  });

  readonly headline = computed(() => {
    const r = this.rank();
    const t = this.fitTotal();
    if (r === 1) return 'Best overall fit for this client’s scenario.';
    if (t >= 85) return `Close second — strong on income, small trade-off vs #1.`;
    if (t >= 75) return `Ranked #${r} — balanced profile with a distinct advantage.`;
    return `Ranked #${r} — included for comparison; review the flags below.`;
  });

  readonly subhead = computed(() => {
    const p = this.product();
    const top = this.peers().find(x => x.fit.total > this.fitTotal());
    if (!top) {
      return `${this.fmtPct(p.fit.income)} on income · ${this.fmtPct(p.fit.fees)} on cost · ${this.fmtPct(p.fit.strength)} on carrier strength.`;
    }
    const incomeDelta = p.estAnnualIncome - top.estAnnualIncome;
    const pFee = p.riderFee ?? 0;
    const tFee = top.riderFee ?? 0;
    const feeDelta = pFee - tFee;
    const dir = incomeDelta >= 0 ? 'ahead of' : 'behind';
    return `${this.money(Math.abs(incomeDelta))} ${dir} ${top.companyName} on annual income · ` +
      `<b>${pFee.toFixed(2)}%</b> vs <b>${tFee.toFixed(2)}%</b> on rider fee ` +
      `(${feeDelta > 0 ? 'higher' : 'lower'}) · ` +
      `<b>${p.surryr}yr surrender</b> vs ${top.surryr}yr.`;
  });

  readonly factor = computed(() => {
    const p = this.product();
    const f = this.scenario()?.ownership === 'Joint' ? p.factorJoint : p.factorSingle;
    return f ?? 0;
  });

  readonly incomeVsTop = computed<number | null>(() => {
    const top = [this.product(), ...this.peers()]
      .sort((a, b) => b.estAnnualIncome - a.estAnnualIncome)[0];
    if (!top || top.gmwbId === this.product().gmwbId) return null;
    return this.product().estAnnualIncome - top.estAnnualIncome;
  });

  readonly rationaleItems = computed(() => {
    const p = this.product();
    const peer = this.bestPeer();
    const out: { tone: 'win' | 'mix' | 'miss'; text: string }[] = [];

    // Peer-aware lead bullets (Stage 04 Compare context). Prepended so the
    // narrative opens with the comparison that matters to the rep, not the
    // generic fit-score bucket.
    if (peer) {
      const incomeDiff = (p.estAnnualIncome ?? 0) - (peer.estAnnualIncome ?? 0);
      if (Math.abs(incomeDiff) >= 100) {
        out.push({
          tone: incomeDiff >= 0 ? 'win' : 'miss',
          text: incomeDiff >= 0
            ? `Beats <b>${peer.companyName.split(' ')[0]}</b> on income by <b>${this.money(incomeDiff)}/yr</b>.`
            : `<b>${this.money(Math.abs(incomeDiff))}/yr</b> behind <b>${peer.companyName.split(' ')[0]}</b> on annual income.`,
        });
      }
      const feeDiff = (peer.riderFee ?? 0) - (p.riderFee ?? 0);
      if (Math.abs(feeDiff) >= 0.05) {
        out.push({
          tone: feeDiff > 0 ? 'win' : 'mix',
          text: feeDiff > 0
            ? `Lower rider fee by <b>${feeDiff.toFixed(2)}%</b> — more of the benefit base keeps working.`
            : `Higher rider fee by <b>${Math.abs(feeDiff).toFixed(2)}%</b> vs best peer.`,
        });
      }
      const surrDiff = (peer.surryr ?? 0) - (p.surryr ?? 0);
      if (Math.abs(surrDiff) >= 1) {
        out.push({
          tone: surrDiff > 0 ? 'win' : 'mix',
          text: surrDiff > 0
            ? `Shorter surrender — <b>${p.surryr}yr</b> vs ${peer.surryr}yr on the top pick.`
            : `Longer surrender — <b>${p.surryr}yr</b> vs ${peer.surryr}yr on the top pick.`,
        });
      }
    }

    // Fallback / supplemental fit-score bullets. Added when no peer or to
    // round out the narrative to 4 bullets total.
    if (out.length < 2) {
      for (const line of p.fit.rationale ?? []) {
        out.push({ tone: 'win', text: line });
      }
    }
    if (p.fit.fees < 60 && !out.some(b => /rider fee/i.test(b.text))) {
      out.push({ tone: 'mix', text: `Rider fee runs higher than peers — <b>${(p.riderFee ?? 0).toFixed(2)}%</b>.` });
    }
    if (p.fit.flexibility < 60 && !out.some(b => /surrender/i.test(b.text))) {
      out.push({ tone: 'mix', text: `Longer surrender schedule — <b>${p.surryr}yr</b>. Walkaway penalty lasts further.` });
    }
    if (p.fit.strength < 70) {
      out.push({ tone: 'miss', text: `Carrier strength below peer median — <b>${p.bestid}</b>.` });
    }
    return out.slice(0, 4);
  });

  /** Best peer by Fit Score total (for the compare-vs-peer bars). */
  private readonly bestPeer = computed<MatchSelection | null>(() => {
    const ps = this.peers();
    if (ps.length === 0) return null;
    return [...ps].sort((a, b) => b.fit.total - a.fit.total)[0];
  });

  readonly bestPeerLabel = computed<string>(() => {
    const peer = this.bestPeer();
    return peer ? `vs ${peer.companyName}` : '';
  });

  /**
   * Three rows of delta bars: income, fee, surrender. Each shows the
   * magnitude of this product's advantage or disadvantage vs the best
   * peer. Fill bar width is proportional to the delta relative to the
   * peer's value (clamped to 100%).
   */
  readonly peerCompareRows = computed<PeerCompareRow[]>(() => {
    const peer = this.bestPeer();
    const p = this.product();
    if (!peer) return [];

    const rows: PeerCompareRow[] = [];

    // Income: higher is better
    const thisInc = p.estAnnualIncome ?? 0;
    const peerInc = peer.estAnnualIncome ?? 0;
    if (thisInc > 0 || peerInc > 0) {
      const diff = thisInc - peerInc;
      const base = Math.max(peerInc, thisInc, 1);
      rows.push({
        key: 'income',
        label: 'Est. annual income',
        thisText: this.money(thisInc),
        peerText: this.money(peerInc),
        deltaText: `${diff >= 0 ? '+' : '−'}${this.money(Math.abs(diff))}`,
        direction: diff >= 0 ? 'pos' : 'neg',
        fillPct: Math.min(100, (Math.abs(diff) / base) * 100 * 4),
      });
    }

    // Rider fee: lower is better
    const thisFee = p.riderFee ?? 0;
    const peerFee = peer.riderFee ?? 0;
    if (thisFee > 0 || peerFee > 0) {
      const diff = peerFee - thisFee;  // positive = this product has lower fee = wins
      const base = Math.max(peerFee, thisFee, 0.01);
      rows.push({
        key: 'fee',
        label: 'Rider fee',
        thisText: `${thisFee.toFixed(2)}%`,
        peerText: `${peerFee.toFixed(2)}%`,
        deltaText: `${diff >= 0 ? '−' : '+'}${Math.abs(peerFee - thisFee).toFixed(2)}%`,
        direction: diff >= 0 ? 'pos' : 'neg',
        fillPct: Math.min(100, (Math.abs(diff) / base) * 100 * 2),
      });
    }

    // Surrender term: shorter is better
    const thisSurr = p.surryr ?? 0;
    const peerSurr = peer.surryr ?? 0;
    if (thisSurr > 0 || peerSurr > 0) {
      const diff = peerSurr - thisSurr;  // positive = this product has shorter surrender = wins
      const base = Math.max(peerSurr, thisSurr, 1);
      rows.push({
        key: 'surrender',
        label: 'Surrender term',
        thisText: `${thisSurr}yr`,
        peerText: `${peerSurr}yr`,
        deltaText: `${diff >= 0 ? '−' : '+'}${Math.abs(diff)}yr`,
        direction: diff >= 0 ? 'pos' : 'neg',
        fillPct: Math.min(100, (Math.abs(diff) / base) * 100 * 2),
      });
    }

    return rows;
  });

  readonly thisPolygon = computed(() => {
    const fit = this.product().fit;
    return this.polygonPoints(fit.income, fit.fees, fit.flexibility, fit.strength);
  });

  readonly topPeerPolygon = computed<string | null>(() => {
    const top = [...this.peers()].sort((a, b) => b.fit.total - a.fit.total)[0];
    if (!top || top.gmwbId === this.product().gmwbId) return null;
    return this.polygonPoints(top.fit.income, top.fit.fees, top.fit.flexibility, top.fit.strength);
  });

  readonly isTopRanked = computed(() => {
    const all = [this.product(), ...this.peers()];
    const top = all.sort((a, b) => b.fit.total - a.fit.total)[0];
    return top?.gmwbId === this.product().gmwbId;
  });

  toggleSection(id: string): void {
    const next = new Set(this._openSections());
    const opening = !next.has(id);
    if (opening) {
      next.add(id);
      this.hydrateSection(id);
    } else {
      next.delete(id);
    }
    this._openSections.set(next);
  }

  private hydrateSection(id: string): void {
    const pid = this.product().productid;
    if (!pid) return;
    const cacheKey = `${id}-${pid}`;
    if (this.hydrated.has(cacheKey)) return;
    this.hydrated.add(cacheKey);

    switch (id) {
      case 'interest': {
        this.interestLoading.set(true);
        this.api.getIAInterest(pid).subscribe({
          next: (res) => {
            const rows = Array.isArray(res) ? res : (res?.data ?? []);
            this.interestData.set(rows);
            this.interestLoading.set(false);
          },
          error: () => this.interestLoading.set(false),
        });
        break;
      }
      case 'charges': {
        this.chargesLoading.set(true);
        this.api.getIASurrWaivers(pid).subscribe({
          next: (res) => {
            const first = res?.surrenders?.[0];
            const schedule: { yr: number; pct: number }[] = [];
            if (first) {
              // yrN fields are stored as literal percents (8.3 = 8.3%), not decimals.
              // Walk until we hit null (schedule ended); include terminal 0 year.
              for (let i = 1; i <= 20; i++) {
                const v = first[`yr${i}`];
                if (v == null) break;
                schedule.push({ yr: i, pct: Number(v) });
              }
            }
            this.chargesData.set({ schedule, waivers: res?.waivers ?? [] });
            this.chargesLoading.set(false);
          },
          error: () => this.chargesLoading.set(false),
        });
        break;
      }
      case 'riders': {
        this.ridersLoading.set(true);
        this.api.getIARiderBenefits(pid).subscribe({
          next: (res) => {
            const b = res?.benefits ?? {};
            const riders: any[] = res?.riders ?? [];
            const selectedRider = riders.find(r => r.pwGmwbId === this.product().gmwbId) ?? riders[0] ?? null;
            this.ridersData.set({
              gmwbCount: b.productGmwbsVersions?.length ?? 0,
              deathCount: b.deathBenefitsVersions?.length ?? 0,
              bonusCount: b.bonusesVersions?.length ?? 0,
              gmabCount: b.gmabsVersions?.length ?? 0,
              gmibCount: b.gmibsVersions?.length ?? 0,
              featuresCount: b.featuresVersions?.length ?? 0,
              selectedRider,
            });
            this.ridersLoading.set(false);
          },
          error: () => this.ridersLoading.set(false),
        });
        break;
      }
      case 'options': {
        this.optionsLoading.set(true);
        this.api.getIAWithdrawalSettlement(pid).subscribe({
          next: (res) => {
            this.optionsData.set({
              withdrawal: res?.withdrawals?.[0] ?? null,
              settlement: res?.settlements?.[0] ?? null,
            });
            this.optionsLoading.set(false);
          },
          error: () => this.optionsLoading.set(false),
        });
        break;
      }
      case 'states': {
        this.statesLoading.set(true);
        this.api.getIAStateVariations(pid).subscribe({
          next: (res) => {
            this.statesData.set({ sections: res?.sections ?? [] });
            this.statesLoading.set(false);
          },
          error: () => this.statesLoading.set(false),
        });
        break;
      }
    }
  }

  /** Percent formatter — values stored as decimals (0.0850 = 8.50%). */
  pct(v: number | null | undefined): string {
    if (v == null || Number.isNaN(Number(v))) return '—';
    return (Number(v) * 100).toFixed(2) + '%';
  }

  /** Percent formatter for values stored as literal percents (8.3 = 8.3%). */
  fmtLiteralPct(v: number | null | undefined): string {
    if (v == null || Number.isNaN(Number(v))) return '—';
    return Number(v).toFixed(1) + '%';
  }

  money(n: number): string {
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1000) {
      return (n < 0 ? '−' : '') + '$' + Math.round(abs).toLocaleString();
    }
    return (n < 0 ? '−' : '') + '$' + abs.toFixed(0);
  }

  private fmtPct(n: number): string {
    return `${Math.round(n)}/100`;
  }

  /** Build an SVG radar polygon from 4 sub-scores (0-100) → 220×220 grid. */
  private polygonPoints(income: number, fees: number, flex: number, carrier: number): string {
    const cx = 110, cy = 110, maxR = 95;
    const r = (score: number) => (Math.max(0, Math.min(100, score)) / 100) * maxR;
    const top    = `${cx},${cy - r(income)}`;
    const right  = `${cx + r(fees)},${cy}`;
    const bottom = `${cx},${cy + r(carrier)}`;
    const left   = `${cx - r(flex)},${cy}`;
    return `${top} ${right} ${bottom} ${left}`;
  }
}
